import { useCallback, useEffect, useState } from 'react';
import { useConnectionState, useToastState } from '../store/useAppStore';

export const useErrorRecovery = () => {
  const { 
    isConnected, 
    authId, 
    setConnectionStatus, 
    setQRCode, 
    setLoading 
  } = useConnectionState();
  const { showToast } = useToastState();
  
  // Track online status reactively
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Clear corrupted state and restart connection
  const recoverConnection = useCallback(async () => {
    try {
      showToast({
        type: 'info',
        title: 'Recuperando Conexão',
        message: 'Tentando recuperar a conexão com o WhatsApp...'
      });

      // Reset connection state
      setConnectionStatus(false);
      setQRCode(null);
      setLoading(true);

      // Clear potentially corrupted data
      localStorage.removeItem('wpp_auth_uuid');
      
      // Force a fresh start
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error during connection recovery:', error);
      showToast({
        type: 'error',
        title: 'Falha na Recuperação',
        message: 'Não foi possível recuperar a conexão. Tente recarregar a página.'
      });
    }
  }, [setConnectionStatus, setQRCode, setLoading, showToast]);

  // Reset app state to initial values
  const resetAppState = useCallback(() => {
    try {
      showToast({
        type: 'info',
        title: 'Reiniciando Aplicação',
        message: 'Restaurando estado inicial da aplicação...'
      });

      // Clear all local storage
      localStorage.clear();
      
      // Reload the page for a fresh start
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error during app state reset:', error);
      showToast({
        type: 'error',
        title: 'Falha no Reset',
        message: 'Não foi possível resetar o estado. Recarregue a página manualmente.'
      });
    }
  }, [showToast]);

  // Force reload with user confirmation
  const forceReload = useCallback(() => {
    const confirmed = window.confirm(
      'Tem certeza que deseja recarregar a aplicação? Isso irá resetar todos os dados não salvos.'
    );
    
    if (confirmed) {
      showToast({
        type: 'info',
        title: 'Recarregando',
        message: 'Recarregando aplicação...'
      });
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [showToast]);

  // Monitor for potential issues - fixed to prevent infinite loops
  useEffect(() => {
    // Check if we have an authId but are not connected for too long
    if (authId && !isConnected) {
      const connectionTimeout = setTimeout(() => {
        showToast({
          type: 'warning',
          title: 'Problema de Conexão',
          message: 'Conexão demorada detectada. Clique para tentar recuperar.',
          duration: 0,
          action: {
            label: 'Recuperar',
            onClick: recoverConnection
          }
        });
      }, 30000); // 30 seconds

      return () => clearTimeout(connectionTimeout);
    }
  }, [authId, isConnected, recoverConnection, showToast]);

  // Handle unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Show toast for unhandled promises
      showToast({
        type: 'error',
        title: 'Erro de Conexão',
        message: 'Detectado problema de conectividade. Clique para recuperar.',
        action: {
          label: 'Recuperar',
          onClick: recoverConnection
        }
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [recoverConnection, showToast]);

  // Handle network status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      showToast({
        type: 'success',
        title: 'Conexão Restaurada',
        message: 'Conexão com a internet foi restaurada.'
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      showToast({
        type: 'error',
        title: 'Sem Conexão',
        message: 'Conexão com a internet foi perdida. Verifique sua rede.',
        duration: 0
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showToast]);

  return {
    recoverConnection,
    resetAppState,
    forceReload,
    isOnline
  };
};

export default useErrorRecovery;